<template>
  <asc-page-loading-indicator
    :loading="
      (isMaintenanceDataLoading || isLookupsLoading) && !apiPageFailure.length
    "
  >
    <v-container fluid class="asc-maintenance-list-container">
      <v-row>
        <v-col cols="12" sm="12">
          <asc-page-header>
            <template #pageTitle>
              <asc-page-title title="Maintenance Issues"></asc-page-title>
            </template>
          </asc-page-header>
        </v-col>
      </v-row>
      <asc-notification
        v-if="getSessionStatus"
        :definition="notification"
        :on-close="handleSessionFlag"
      />

      <asc-maintenance-list-template
        :title="getMaintenanceTitle"
        :values="mappedMaintenanceGrid"
        :on-open="handleOpenClick"
        :internal-user="isInternalUser"
        :maintenance-export-table-grid-permission="
          maintenanceExportTableGridPermission
        "
        :maintenance-grid-permission="maintenanceGridPermission"
        :grid-columns="getGridColumns"
        :on-mx-rec-alerts-export="handleMxRecAlertsExport"
        :on-add-manual-mx-rec="handleAddManualMxRec"
        export-button-visible
        :export-menu-items="getExportMenuItems"
        :persistent-grid-id="getPersistentGridId"
        :has-user-airline-agreement="hasUserAirlineAgreement"
        @onAddRepeaterIssue="handleAddRepeaterIssue"
      ></asc-maintenance-list-template>

      <template v-if="isRemovalsTimelineVisible">
        <asc-removals-timeline-template
          :removals-data="removalsDataByTail"
          :removals-filter-fields="removalsFilterFields"
          :filter-lookups="removalsLookups"
          :on-filters-submit="handleTimelineSubmit"
          :maintenance-removals-timeline-permission="
            maintenanceRemovalsTimelinePermission
          "
        ></asc-removals-timeline-template>
      </template>

      <asc-export-dialog
        title="mx rec data export to excel"
        header="Date ranges are limited to a 45 day period."
        export-type="mxrec"
        :show-dialog="mxRecAlertsExportDialogVisible"
        :items="mxRecAlertsExportDailogItems"
        :on-close="handleMxRecExportDialogclose"
        :on-accept="handleMxRecAlertsExportAccept"
      ></asc-export-dialog>

      <asc-add-manual-mx-rec-dialog
        :show-dialog="addManualMxRecDialogVisible"
        :loading="isLookupsLoading"
        :lookups="lookups"
        :on-close="handleAddManualMxRecDialogClose"
        :on-manual-mx-rec-add="handleManualMxRecAdd"
      ></asc-add-manual-mx-rec-dialog>

      <asc-add-repeater-issue-dialog
        :show-dialog="addNewRepeaterIssueDialogVisible"
        :loading="isLookupsLoading || isAddnewRepeatersIssueLoading"
        :tails="getRepeaterTails"
        :ata-chapters="mappedLuAtaChaptersWithZeroes"
        :on-close="handleAddRepeaterIssueDialogClose"
        @onOpenIssue="handleCreateNewRepeaterIssue"
      />

      <asc-loading-indicator-dialog
        text="Exporting"
        :show-dialog="isMxRecAlertsExportLoading"
        :loading="true"
      ></asc-loading-indicator-dialog>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscMxRecPermissionMixin from '@/components/mixins/AscMxRecPermissionMixin'
import AscAddManualMxRecDialog from '@/components/molecules/AscAddManualMxRecDialog'
import AscAddRepeaterIssueDialog from '@/components/molecules/AscAddRepeaterIssueDialog'
import AscExportDialog from '@/components/molecules/AscExportDialog'
import AscLoadingIndicatorDialog from '@/components/molecules/AscLoadingIndicatorDialog'
import AscNotification from '@/components/molecules/AscNotification'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import {
  AscMaintenanceListTemplate,
  AscRemovalsTimelineTemplate,
} from '@/components/templates'
import {
  ANALYTIC_MODULE_CONSTS as AMC,
  GLOBAL_FILTER_CONSTS as GF,
  LOOKUP_CONSTS as LC,
  MAINTENANCE_ISSUES_CONSTS as MIC,
  USER_CONSTS as UC,
} from '@/stores/consts'
import {
  COLLINS_PHM_TIER_3,
  COLLINS_PHM_TIER_4,
  REMOVALS_FILTER_FIELDS,
  REPEATERS,
} from '@/utilities/appConsts'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import { getMaintenanceGridColumns } from '@/utilities/maintenance'
import { Removals } from '@/utilities/removalsApi'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

export default {
  name: 'AscMaintenanceListContainer',

  components: {
    AscPageTitle,
    AscPageHeader,
    AscNotification,
    AscPageLoadingIndicator,
    AscMaintenanceListTemplate,
    AscExportDialog,
    AscAddManualMxRecDialog,
    AscLoadingIndicatorDialog,
    AscAddRepeaterIssueDialog,
    AscRemovalsTimelineTemplate,
  },
  mixins: [AscMxRecPermissionMixin],

  data() {
    return {
      isMaintenanceDataLoading: false,
      maintenaceData: [],
      notification: [
        {
          color: 'warning',
          isActive: true,
          text: 'Your session has expired without saved changes.',
          type: 'warning',
        },
      ],
      mxRecAlertsExportDailogItems: [
        {
          title: 'Export Mx Rec Data by "Date Last Updated"',
          value: 'UpdatedDate',
        },
        {
          title: 'Export Mx Rec Data by "Date Created"',
          value: 'CreatedDate',
        },
      ],
      mxRecAlertsExportDialogVisible: false,
      isLookupsLoading: false,
      addManualMxRecDialogVisible: false,
      isAddnewRepeatersIssueLoading: false,
      addNewRepeaterIssueDialogVisible: false,
      removalsFilterFields: REMOVALS_FILTER_FIELDS,
      repeaters: REPEATERS,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapState(MIC.NAMESPACE, [
      'isMxRecAlertsExportLoading',
      'removalsDataByTail',
    ]),
    ...mapGetters(MIC.NAMESPACE, [
      'getSessionStatus',
      'mappedMaintenanceGrid',
      'manualMxRecSources',
    ]),
    ...mapGetters(UC.NAMESPACE, [
      'isInternalUser',
      'isIssueInvestigator',
      'isIssueReader',
      'mappedUserAirlines',
      'isAirlineUser',
      'maintenanceRemovalsTimelinePermission',
      'maintenanceExportTableGridPermission',
      'maintenanceGridPermission',
    ]),
    ...mapGetters(LC.NAMESPACE, [
      'mappedUserMasterModels',
      'mappedLuTails',
      'mappedLuAtaChapters',
      'mappedLuAtaChaptersWithZeroes',
      'mappedLuSystemsByAirline',
      'mappedLuComponentsByAirline',
    ]),
    ...mapGetters(AMC.NAMESPACE, ['mappedAnalyticModules']),

    hasUserAirlineAgreement() {
      const isRepeaterAgreement = this.mappedUserAirlines.find(x =>
        x.phmAgreementTypes.includes(REPEATERS),
      )
      return isRepeaterAgreement != undefined ? true : false
    },
    isRemovalsTimelineVisible() {
      return this.isAirlineUser && AppFeatureFlags.getRemovalsTimeline()
    },

    getMaintenanceTitle() {
      return this.isInternalUser
        ? 'Maintenance & Alerts'
        : 'Maintenance Recommendations'
    },

    getExportMenuItems() {
      const menuItems = [
        {
          title: 'Export Maintenance Table Data',
          value: 0,
        },
      ]

      if (this.isEditPermissionInvestigate && this.isInternalUser) {
        menuItems.push({
          title: 'Export Issues Alert Data',
          value: 1,
        })
      }

      return menuItems
    },

    getRepeaterTails() {
      return this.mappedLuTails.filter(tail => {
        return tail.airlineAgreementTypes.includes(this.repeaters)
      })
    },

    getGridColumns() {
      return getMaintenanceGridColumns(
        this.isInternalUser,
        this.mappedUserAirlines,
        this.mappedUserMasterModels,
      )
    },
    removalsLookups() {
      const toRet = []
      const luTails = this.mappedLuTails.filter(
        x => x.airlineTierTypeId == COLLINS_PHM_TIER_4,
      ) // RESTRICT REMOVALS TO TIER 4

      toRet.push({
        id: 'tail',
        child: 'system',
        filterId: 'masterModelId',
        items: luTails,
      })

      return toRet
    },
    lookups() {
      const toRet = []
      const luTails = this.mappedLuTails.filter(
        x =>
          x.airlineTierTypeId == COLLINS_PHM_TIER_3 ||
          x.airlineTierTypeId == COLLINS_PHM_TIER_4,
      ) // RESTRICT USERS TO CREATE MANUAL MX REC ONLY FOR TIER3/4

      toRet.push({
        id: 'tail',
        child: 'system',
        filterId: 'masterModelId',
        items: luTails,
      })

      toRet.push({
        parent: 'tail',
        id: 'system',
        child: 'component',
        filterId: 'masterModelId',
        items: this.mappedLuSystemsByAirline,
      })

      toRet.push({
        id: 'component',
        parent: 'system',
        child: 'analyticModule',
        filterId: 'systemId',
        items: this.mappedLuComponentsByAirline,
      })

      toRet.push({
        id: 'analyticModule',
        parent: 'component',
        filterId: 'componentId',
        items: this.mappedAnalyticModules,
      })

      toRet.push({
        id: 'source',
        items: this.manualMxRecSources,
      })

      return toRet
    },
    getPersistentGridId() {
      return 'asc-maintenance-grid'
    },
  },

  watch: {
    globalFilter: {
      deep: true,
      handler() {
        this[MIC.UPDATE_REMOVALS_DATA_BY_TAIL]([])
        this.getMaintenancePageData()
      },
    },
  },

  created() {
    this.getMaintenancePageData()
  },

  async beforeDestroy() {
    await this[MIC.GET_SESSION_EXPIRATION](false)
  },

  methods: {
    ...mapActions(MIC.NAMESPACE, [
      MIC.GET_MAINTENANCE_GRID,
      MIC.GET_SESSION_EXPIRATION,
      MIC.GET_MXREC_ALERTS_EXPORT_GIVEN_OPTIONS,
      MIC.GET_ISSUE_SOURCE_OPTIONS,
      MIC.ADD_MANUAL_MAINTENANCE_ISSUE,
      MIC.GET_REMOVALS_DATA_BY_TAIL,
      MIC.CREATE_NEW_REPEATER_ISSUE,
    ]),

    ...mapActions(LC.NAMESPACE, [
      LC.GET_SYSTEMS,
      LC.GET_COMPONENTS,
      LC.GET_ATA_CHAPTERS,
    ]),

    ...mapActions(AMC.NAMESPACE, [AMC.GET_ANALYTIC_MODULES]),

    ...mapMutations(MIC.NAMESPACE, [MIC.UPDATE_REMOVALS_DATA_BY_TAIL]),

    async getMaintenanceData() {
      this.isMaintenanceDataLoading = true
      await Promise.all([this[MIC.GET_MAINTENANCE_GRID]()])
      this.isMaintenanceDataLoading = false
    },

    handleOpenClick() {
      // console.log('navigate to XXX ', item)
    },

    handleSessionFlag() {
      this.notification[0].isActive = false
      this[MIC.GET_SESSION_EXPIRATION](false)
    },

    handleMxRecExportDialogclose() {
      this.mxRecAlertsExportDialogVisible = false
    },

    handleMxRecAlertsExport() {
      this.mxRecAlertsExportDialogVisible = true
    },

    async handleTimelineSubmit() {
      if (this.maintenanceRemovalsTimelinePermission) {
        const rapi = new Removals(this.removalsFilterFields)
        const selector = rapi.getSelector()
        this.isMaintenanceDataLoading = true
        await this[MIC.GET_REMOVALS_DATA_BY_TAIL](selector)
        this.isMaintenanceDataLoading = false
      }
    },

    handleMxRecAlertsExportAccept(data) {
      const payload = {
        internal: this.isInternalUser,
        startDate: data?.dates?.startDate,
        endDate: data?.dates?.endDate,
        dateFilterOn: data?.value,
      }

      this[MIC.GET_MXREC_ALERTS_EXPORT_GIVEN_OPTIONS](payload)
    },

    async getLookups() {
      this.isLookupsLoading = true
      await Promise.all([
        this[MIC.GET_ISSUE_SOURCE_OPTIONS](),
        this[LC.GET_SYSTEMS](),
        this[LC.GET_COMPONENTS](),
        this[AMC.GET_ANALYTIC_MODULES](),
        this[LC.GET_ATA_CHAPTERS](),
      ])

      this.isLookupsLoading = false
    },

    handleAddManualMxRec() {
      this.addManualMxRecDialogVisible = true
    },

    handleAddManualMxRecDialogClose() {
      this.addManualMxRecDialogVisible = false
    },

    handleAddRepeaterIssueDialogClose() {
      this.addNewRepeaterIssueDialogVisible = false
    },

    async handleManualMxRecAdd(mxRec) {
      this.isLookupsLoading = true
      const mxRecToAdd = {
        ...mxRec,
        status: 1, // New Status
      }

      const manualMxRecAddStatusId = await this[
        MIC.ADD_MANUAL_MAINTENANCE_ISSUE
      ](mxRecToAdd)
      this.isLookupsLoading = false
      if (manualMxRecAddStatusId) {
        this.addManualMxRecDialogVisible = false
        this.$router.push({
          name: 'Mx Recs',
          params: {
            issueId: manualMxRecAddStatusId,
          },
        })
      }
    },

    handleAddRepeaterIssue() {
      this.addNewRepeaterIssueDialogVisible = true
    },

    async handleCreateNewRepeaterIssue(data) {
      this.isAddnewRepeatersIssueLoading = true

      const repeaterAddStatusId = await this[MIC.CREATE_NEW_REPEATER_ISSUE]({
        tailNumber: data.tail.text,
        ataChapters: data.ataChapters,
      })
      this.isAddnewRepeatersIssueLoading = false
      if (repeaterAddStatusId) {
        this.addNewRepeaterIssueDialogVisible = false

        this.$router.push({
          name: 'Mx Recs',
          params: {
            issueId: repeaterAddStatusId,
          },
        })
      }
    },

    async getMaintenancePageData() {
      this.getLookups()
      await this.getMaintenanceData()
    },
  },
}
</script>
